import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  AnimatePresence,
  IgAvatar,
  IgTooltip,
  IgTypography,
  RevealMotion,
} from "@intelligems/ig-theme";
import { IconButton } from "@mui/material";
import Grid from "@mui/material/Grid";
import { keyframes } from "@mui/system";
import { toGqlId } from "@src/api/formatters/shopify";
import { gqlProductImage } from "@src/api/types/graphql";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { useFetchProductImagesLazyQuery } from "@src/generated";
import NoImage from "@src/images/no_image.png";
import { ClipboardCopy } from "@src/utils/ClipboardCopy";

interface SelectedProduct {
  id: string;
  title?: string | null | undefined;
  image?: string | null | undefined;
}

interface SelectedProductsPreviewProps {
  isView?: boolean;
  products: SelectedProduct[];
  onDelete: (productId: string) => void;
}

export const SelectedProductsPreview = ({
  isView,
  products,
  onDelete,
}: SelectedProductsPreviewProps) => {
  const { isReadOnly } = useReadOnlyMode();

  return (
    <AnimatePresence>
      {products.length > 0 && (
        <RevealMotion>
          <Grid
            container
            columnSpacing={2}
            direction={"row"}
            alignItems={"center"}
            sx={{ maxWidth: "600px", marginTop: "5px" }}
          >
            {products.slice(0, 7).map((product) => (
              <ProductPreview
                key={product.id}
                product={product}
                isView={isView}
                onDelete={onDelete}
                skipImageFetch={isReadOnly}
              />
            ))}
            {products.length > 7 ? (
              <Grid sx={{ marginTop: "-25px" }}>
                <IgTypography>+ {products.length - 7} products</IgTypography>
              </Grid>
            ) : null}
          </Grid>
        </RevealMotion>
      )}
    </AnimatePresence>
  );
};

interface ProductPreviewProps {
  product: SelectedProduct;
  isView?: boolean;
  onDelete: (productId: string) => void;
  skipImageFetch?: boolean;
}

interface ProductAvatarProps {
  product: SelectedProduct;
  imageSrc: string;
}

const pulseOnce = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
`;

const ProductAvatar = ({ product, imageSrc }: ProductAvatarProps) => {
  const [isPulsing, setIsPulsing] = useState(false);
  const title = `${product.title ?? "Product"}: (${product.id})`;

  const handleClick = () => {
    setIsPulsing(true);
    setTimeout(() => setIsPulsing(false), 250);
    toast.success(`Copied Product ID (${product.id}) to clipboard`, {
      autoClose: 1500,
    });
  };

  return (
    <ClipboardCopy
      copyText={product.id}
      showUrl={false}
      copyButton={
        <IgTooltip placement={"top"} title={title}>
          <IgAvatar
            border={true}
            variant={"rounded"}
            alt={product?.title || ""}
            src={imageSrc}
            onClick={handleClick}
            sx={{
              cursor: "pointer",
              animation: isPulsing ? `${pulseOnce} 250ms ease-out` : "none",
            }}
          />
        </IgTooltip>
      }
    />
  );
};

/**
 * ProductPreviewReadOnly - used in share app (no Apollo context)
 * Simply displays the image from product data or a placeholder
 */
const ProductPreviewReadOnly = ({
  product,
  isView,
  onDelete,
}: Omit<ProductPreviewProps, "skipImageFetch">) => {
  return (
    <Grid key={`avatar-preview-${product.id}`} sx={{ textAlign: "center" }}>
      <Grid>
        <ProductAvatar product={product} imageSrc={product.image ?? NoImage} />
      </Grid>
      {!isView && (
        <Grid>
          <IconButton
            onClick={() => {
              onDelete(product.id);
            }}
            size={"small"}
          >
            <X size={18} />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
};

/**
 * ProductPreviewWithFetch - used in main app (has Apollo context)
 * Fetches product images via GraphQL if not available in product data
 */
const ProductPreviewWithFetch = ({
  product,
  isView,
  onDelete,
}: Omit<ProductPreviewProps, "skipImageFetch">) => {
  const [image, setImage] = useState(product.image);
  const [getProductsImage] = useFetchProductImagesLazyQuery();

  useEffect(() => {
    setImage(product.image);

    const getImage = async () => {
      if (!product.image) {
        const resp = await getProductsImage({
          variables: {
            ids: [toGqlId(product.id, "Product")],
          },
        });
        if (resp.data) {
          const queriedNode = resp.data.nodes[0] as gqlProductImage | null;
          setImage(queriedNode?.featuredImage?.url || NoImage);
        }
      }
    };
    void getImage();
  }, [product.image, product.id, getProductsImage]);

  return (
    <Grid key={`avatar-preview-${product.id}`} sx={{ textAlign: "center" }}>
      <Grid>
        <ProductAvatar product={product} imageSrc={image ?? NoImage} />
      </Grid>
      {!isView && (
        <Grid>
          <IconButton
            onClick={() => {
              onDelete(product.id);
            }}
            size={"small"}
          >
            <X size={18} />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
};

const ProductPreview = ({
  product,
  isView,
  onDelete,
  skipImageFetch,
}: ProductPreviewProps) => {
  // Use read-only version without Apollo hook when in share app
  if (skipImageFetch) {
    return (
      <ProductPreviewReadOnly
        product={product}
        isView={isView}
        onDelete={onDelete}
      />
    );
  }

  return (
    <ProductPreviewWithFetch
      product={product}
      isView={isView}
      onDelete={onDelete}
    />
  );
};
